import { useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AttendanceLocal } from "../../../../../../../../../../../@types/attendance-local";
import { useAttendanceLocalDepartmentList } from "../../../../../../../../../../../services/api-tansquery/attendance-local/department/list";
import { useDebouncedValue } from "../../../../../../../../../../../hooks/useDebounce";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../@types/attendance-local-department";

type Props = {
    slug: AttendanceLocal["slug_unidade"];
    field: ControllerRenderProps<{
        usuario?: {
            first_name?: string;
            id?: number;
            last_name?: string;
            username?: string;
        };
        setor_data?: {
            setor?: {
                id?: number;
                nome?: string;
                sigla?: string;
            };
        };
        misto?: boolean;
    }, "setor_data">
    fieldState: ControllerFieldState
    style?: React.CSSProperties;
}

export default function DepartmentField({ slug, field, fieldState, style }: Props) {
    const [search, setSearch] = useState<string>();
    const debouncedSearch = useDebouncedValue(search, 500);
    const { data: departmentList, isLoading } = useAttendanceLocalDepartmentList({
        search: debouncedSearch, unidade_slug: slug, page: 1
    });

    const options = useMemo(() => {
        return departmentList?.results || [];
    }, [departmentList]);

    const selected = useMemo(() => {
        return departmentList?.results.find(
            (department) => department.setor.id === field.value?.setor.id
        ) || null;
    }, [departmentList, field.value]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value.trim() === "" ? undefined : value);
        };
    }

    function handleChange(_, newValue: AttendanceLocalDepartment | null) {
        if (!newValue) {
            field.onChange(null);
        } else {
            field.onChange({
                setor: newValue.setor
            });
        }
    }

    return (
        <Autocomplete
            style={style}
            freeSolo={false}
            options={options}
            value={selected}
            onChange={handleChange}
            onInputChange={handleInputChange}
            getOptionLabel={(option) => option.setor.nome || ""}
            getOptionSelected={(option, value) => option.setor.id === value.setor.id}
            loading={isLoading}
            loadingText="Carregando setores..."
            noOptionsText="Nenhum setor encontrado"
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Setor"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Selecione um setor"}
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
